<template>
    <div class="max-w-6xl mx-auto px-0 py-2">
        <div class="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700">
            <div class="p-2 md:p-6 space-y-6">
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <!-- Product Name -->
                    <div>
                        <label class="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                            Product Name*
                        </label>
                        <div class="space-y-1">
                            <t-input
                                v-model="form.name"
                                type="text"
                                placeholder="Product Name"
                                required
                                :variant="errors('name') ? 'danger' : ''"
                            />
                            <p
                                v-if="errors('name')"
                                class="text-xs text-red-500"
                            >
                                {{ errors('name') }}
                            </p>
                        </div>
                    </div>

                    <!-- Product ALU and Status -->
                    <div class="flex flex-col md:flex-row gap-4 w-full">
                        <!-- Product ALU (takes remaining width) -->
                        <div class="flex-1">
                            <label class="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                                Product ALU
                            </label>
                            <div class="space-y-1">
                                <t-input
                                    v-model="form.alu"
                                    type="text"
                                    placeholder="Product ALU"
                                    required
                                    :variant="errors('name') ? 'danger' : ''"
                                />
                                <p
                                    v-if="errors('alu')"
                                    class="text-xs text-red-500"
                                >
                                    {{ errors('alu') }}
                                </p>
                            </div>
                        </div>
                        <!-- Product Status (fixed width) -->
                        <div class="w-full md:w-1/3">
                            <label class="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                                Product Status*
                            </label>
                            <t-rich-select
                                v-model="form.productStatus"
                                :options="['New', 'Used', 'Refurbished']"
                                placeholder="Product Status"
                                :clearable="true"
                                :variant="errors('product_status') ? 'danger' : ''"
                            />
                            <p
                                v-if="errors('product_status')"
                                class="text-xs text-red-500"
                            >
                                {{ errors('product_status') }}
                            </p>
                        </div>
                    </div>

                    <!-- Product Cost Price -->
                    <div>
                        <label class="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                            Cost Price*
                        </label>
                        <div class="space-y-1">
                            <div class="flex">
                                <span
                                    class="inline-flex items-center px-3 text-sm text-gray-500 border border-r-0 border-gray-300 rounded-l-md bg-gray-50"
                                >
                                    {{ config(common.currency, $page.props.user) }}
                                </span>
                                <t-input
                                    v-model="form.costPrice"
                                    class="rounded-l-none"
                                    type="number"
                                    placeholder="Cost Price"
                                    :variant="errors('cost_price') ? 'danger' : ''"
                                />
                            </div>
                            <p
                                v-if="errors('cost_price')"
                                class="text-xs text-red-500"
                            >
                                {{ errors('cost_price') }}
                            </p>
                        </div>
                    </div>

                    <!-- Product Price -->
                    <div>
                        <label class="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                            Selling Price*
                        </label>
                        <div class="space-y-1">
                            <div class="flex">
                                <span
                                    class="inline-flex items-center px-3 text-sm text-gray-500 border border-r-0 border-gray-300 rounded-l-md bg-gray-50"
                                >
                                    {{ config(common.currency, $page.props.user) }}
                                </span>
                                <t-input
                                    v-model="form.price"
                                    class="rounded-l-none"
                                    type="number"
                                    placeholder="Selling Price"
                                    :variant="errors('price') ? 'danger' : ''"
                                />
                            </div>
                            <p
                                v-if="errors('price')"
                                class="text-xs text-red-500"
                            >
                                {{ errors('price') }}
                            </p>
                        </div>
                    </div>

                    <!-- available stock -->
                    <div v-if="!editData">
                        <label class="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                            Available Stock*
                        </label>
                        <div class="space-y-1">
                            <t-input
                                v-model="form.availableStock"
                                type="number"
                                placeholder="Available Stock"
                                :variant="errors('available_stock') ? 'danger' : ''"
                            />
                            <p
                                v-if="errors('available_stock')"
                                class="text-xs text-red-500"
                            >
                                {{ errors('available_stock') }}
                            </p>
                        </div>
                    </div>

                    <div v-else></div>

                    <!-- product is_deal -->
                    <div>
                        <label class="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300"> Deal* </label>
                        <div class="space-y-1">
                            <div>
                                <t-toggle
                                    v-model="form.isDeal"
                                    :checked="true"
                                />
                            </div>
                            <p
                                v-if="errors('is_deal')"
                                class="text-xs text-red-500"
                            >
                                {{ errors('is_deal') }}
                            </p>
                        </div>
                    </div>

                    <!-- product parent category -->
                    <div>
                        <label class="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                            Category*
                        </label>
                        <div class="space-y-1">
                            <t-rich-select
                                v-model="parentCategoryId"
                                :options="categoriesList"
                                :clearable="true"
                                value-attribute="id"
                                text-attribute="name"
                                placeholder="Select Category"
                            />
                        </div>
                    </div>

                    <!-- product child category -->
                    <div>
                        <label class="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                            Child Category*
                        </label>
                        <div class="space-y-1">
                            <t-rich-select
                                v-model="form.categoryId"
                                :options="
                                    childCategoriesList.filter((category) => category.parent_id === parentCategoryId)
                                "
                                :clearable="true"
                                value-attribute="id"
                                text-attribute="name"
                                placeholder="Select Category"
                                :variant="errors('category_id') ? 'danger' : ''"
                            />
                            <p
                                v-if="errors('category_id')"
                                class="text-xs text-red-500"
                            >
                                {{ errors('category_id') }}
                            </p>
                        </div>
                    </div>

                    <!-- product brand -->
                    <div>
                        <label class="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300"> Brand* </label>
                        <div class="space-y-1">
                            <t-rich-select
                                v-model="form.brandId"
                                :options="brandsList"
                                :clearable="true"
                                value-attribute="id"
                                text-attribute="name"
                                placeholder="Brand Name"
                                :variant="errors('brand_id') ? 'danger' : ''"
                            />
                            <p
                                v-if="errors('brand_id')"
                                class="text-xs text-red-500"
                            >
                                {{ errors('category_id') }}
                            </p>
                        </div>
                    </div>

                    <!-- new brand if brand doesnt exist in brand list above -->
                    <div v-if="form.brandId === 0">
                        <label class="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                            New Brand Name*
                        </label>
                        <div class="space-y-1">
                            <t-input
                                v-model="form.brandName"
                                placeholder="Brand Name"
                                :variant="errors('brand_name') ? 'danger' : ''"
                            />
                            <p
                                v-if="errors('brand_name')"
                                class="text-xs text-red-500"
                            >
                                {{ errors('brand_name') }}
                            </p>
                        </div>
                    </div>
                </div>
                <!-- image list -->
                <div
                    v-if="existingImages.length"
                    class="flex w-full gap-4 mt-6"
                >
                    <div
                        v-for="image in existingImages"
                        :key="'product-image-' + image.id"
                        class="relative flex w-1/3 group"
                    >
                        <div class="absolute items-center justify-center hidden w-full h-full group-hover:flex">
                            <div class="w-full h-full bg-black rounded opacity-50" />
                            <div
                                class="absolute"
                                @click="deleteImage(image)"
                            >
                                <DeleteIcon class="w-8 text-red-500 cursor-pointer fill-current" />
                            </div>
                        </div>
                        <img
                            :alt="form.name"
                            class="object-cover object-center w-full h-full rounded"
                            :src="image.image_path"
                        />
                    </div>
                </div>

                <!-- fileupload -->
                <div>
                    <FileUpload
                        :multiple="true"
                        @filesUploaded="attachFilesToForm"
                    />
                </div>

                <!-- Description Section -->
                <Disclosure
                    class="mt-6"
                    :defaultOpen="false"
                >
                    <template #title>
                        <h2 class="text-lg font-semibold text-gray-800 dark:text-white">Description</h2>
                    </template>
                    <div class="mt-2">
                        <Editor v-model="form.details" />
                    </div>
                </Disclosure>

                <!-- Shipping Section -->
                <Disclosure
                    class="mt-6"
                    :defaultOpen="false"
                >
                    <template #title>
                        <h2 class="text-lg font-semibold text-gray-800 dark:text-white">
                            Shipping and Delivery Detail
                        </h2>
                    </template>
                    <div class="mt-2">
                        <Editor v-model="form.shippingAndDelivery" />
                    </div>
                </Disclosure>

                <!-- Payment Section -->
                <Disclosure
                    class="mt-6"
                    :defaultOpen="false"
                >
                    <template #title>
                        <h2 class="text-lg font-semibold text-gray-800 dark:text-white">Payments and Returns Detail</h2>
                    </template>
                    <div class="mt-2">
                        <Editor v-model="form.paymentsAndReturns" />
                    </div>
                </Disclosure>
            </div>
        </div>
    </div>
</template>

<script>
    import FileUpload from './FileUpload.vue';
    import Product from '../Models/Product';
    import DeleteIcon from './Icons/DeleteIcon.vue';
    import common from '../common';
    import Editor from './Editor.vue';
    import Disclosure from '@/Components/Disclosure.vue';

    export default {
        name: 'ProductsCreate',
        components: {
            FileUpload,
            DeleteIcon,
            Editor,
            Disclosure,
        },

        props: {
            editData: {
                type: Object,
                default: null,
            },
        },

        data() {
            return {
                common,
                form: new Product(),
                oldForm: new Product(),
                parentCategoryId: null,
                categoriesList: [],
                childCategoriesList: [],
                brandsList: [],
                existingImages: [],
            };
        },

        watch: {
            form: {
                deep: true,
                handler(newVal) {
                    const dirty = JSON.stringify(newVal) !== JSON.stringify(this.oldForm);
                    this.$emit('update:isDirty', dirty);
                    // this.submitForm();
                },
            },

            editData: {
                handler(newVal) {
                    this.existingImages = this.editData.images;
                },
                deep: true,
            },
        },

        async mounted() {
            await this.fetchCategories();
            await this.fetchBrands();
            await this.initializeComponent();
        },

        methods: {
            async initializeComponent() {
                if (this.editData) {
                    const selectedCategory = this.childCategoriesList.find(
                        (category) => parseInt(category.id) === parseInt(this.editData.category_id),
                    );
                    const parentCategory = this.categoriesList.find(
                        (category) => parseInt(category.id) === parseInt(selectedCategory.parent_id),
                    );
                    this.parentCategoryId = parseInt(parentCategory.id);

                    console.log(this.editData.cost_price);

                    this.form.name = this.editData.name;
                    this.form.alu = this.editData.alu;
                    this.form.costPrice = this.editData.cost_price;
                    this.form.price = this.editData.price;
                    this.form.salePrice = this.editData.sale_price;
                    this.form.categoryId = this.editData.category_id;
                    this.form.details = this.editData.details;
                    this.form.productStatus = this.editData.product_status;
                    this.form.brandId = this.editData.brand_id;
                    this.form.shippingAndDelivery = this.editData.shipping_and_delivery;
                    this.form.paymentsAndReturns = this.editData.payments_and_returns;
                    this.form.images = [];
                    this.existingImages = this.editData.images;
                    this.form.isDeal = !!this.editData.is_deal;

                    console.log(this.form.costPrice);
                } else {
                    this.form.name = '';
                    this.form.alu = '';
                    this.form.costPrice = '';
                    this.form.price = '';
                    this.form.salePrice = '';
                    this.form.availableStock = '';
                    this.form.categoryId = null;
                    this.form.details = '<p></p>';
                    this.form.productStatus = null;
                    this.form.brandId = null;
                    this.form.shippingAndDelivery = '<p></p>';
                    this.form.paymentsAndReturns = '<p></p>';
                    this.form.images = [];
                    this.form.isDeal = true;
                }
                this.oldForm = { ...this.form };
            },

            async submitForm(done) {
                const payload = this.buildPayload();
                const finish = () => {
                    if (typeof done === 'function') {
                        done(); // tell parent we're done
                    }
                };
                this.editData ? await this.update(payload, finish) : await this.save(payload, finish);
            },

            buildPayload() {
                const formData = new FormData();
                Object.keys(this.form).forEach((item) => {
                    if (this.form[item] !== undefined) {
                        if (
                            item !== 'costPrice' &&
                            item !== 'salePrice' &&
                            item !== 'categoryId' &&
                            item !== 'brandId' &&
                            item !== 'brandName' &&
                            item !== 'paymentsAndReturns' &&
                            item !== 'shippingAndDelivery' &&
                            item !== 'images' &&
                            item !== 'productStatus' &&
                            item !== 'availableStock' &&
                            item !== 'isDeal'
                        ) {
                            formData.append(item, this.form[item]);
                        } else if (item === 'costPrice') {
                            formData.append('cost_price', this.form[item] || null);
                        } else if (item === 'salePrice') {
                            formData.append('sale_price', this.form[item] || null);
                        } else if (item === 'categoryId') {
                            formData.append('category_id', this.form[item]);
                        } else if (item === 'brandId' && parseInt(this.form[item]) !== 0) {
                            formData.append('brand_id', this.form[item]);
                        } else if (item === 'brandName' && !this.form.brandId) {
                            formData.append('brand_name', this.form[item]);
                        } else if (item === 'isDeal') {
                            formData.append('is_deal', this.form[item] ? 1 : 0);
                        } else if (item === 'paymentsAndReturns') {
                            formData.append('payments_and_returns', this.form[item]);
                        } else if (item === 'shippingAndDelivery') {
                            formData.append('shipping_and_delivery', this.form[item]);
                        } else if (item === 'productStatus') {
                            formData.append('product_status', this.form[item]);
                        } else if (item === 'availableStock') {
                            formData.append('available_stock', this.form[item]);
                        } else if (item === 'images' && this.form.images.length) {
                            for (let i = 0; i < this.form.images.length; i++) {
                                const file = this.form.images[i];
                                formData.append(`images[${i}]`, file);
                            }
                        }
                    }
                });
                if (this.editData) {
                    formData.append('_method', 'PUT');
                }

                return formData;
            },

            async save(payload, done) {
                this.$inertia.post(route('products.store'), payload, {
                    onSuccess: () => {
                        this.$emit('forceClose');
                    },
                    onFinish: () => {
                        if (done) done();
                    },
                });
            },

            async update(payload, done) {
                this.$inertia.post(route('products.update', this.editData.slug), payload, {
                    onSuccess: () => {
                        this.$emit('forceClose');
                    },
                    onFinish: () => {
                        if (done) done();
                    },
                });
            },

            attachFilesToForm(files) {
                this.form.images = files;
            },

            deleteImage(image) {
                this.$inertia.delete(route('images.delete', image.id));
            },

            async fetchCategories() {
                await this.$axios.get(route('categories.userCategories')).then((res) => {
                    this.categoriesList = res.data.categories;
                    this.childCategoriesList = this.getChildCategories(res.data.categories);
                });
            },

            getChildCategories(parentCategories) {
                const tempChildrenList = [];
                parentCategories.forEach((parentCategory) => {
                    if (parentCategory.all_children.length) {
                        parentCategory.all_children.forEach((childCategory) => {
                            tempChildrenList.push(childCategory);
                        });
                    }
                });
                return tempChildrenList;
            },

            async fetchBrands() {
                await this.$axios.get(route('brands.userBrands')).then((res) => {
                    this.brandsList = [...res.data.brands, { id: 0, name: 'Others' }];
                });
            },
        },
    };
</script>
